import { type ReactNode } from 'react';
import { clsx } from 'clsx';
import { CampaignCardWrapper, PNBadge, ExtBadge, CampaignStatusBadge } from './CampaignBadges';
import { Badge } from '@/shared/ui/Badge';
import { formatConversions, formatCurrency, formatDate } from '@/shared/utils/format';
import { channelLabel } from '../constants';
import type { ChannelLaunch } from './externalCampaignData';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './CampaignCard.module.css';

interface CampaignCardProps {
  variant: 'pn' | 'external';
  name: string;
  objective?: string | null;
  status: string;
  monthlyBudget?: number;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  cpa?: number | null;
  launches?: ChannelLaunch[];
  onClick?: () => void;
  recSlot?: ReactNode;
  /** Removed on the platform — the card is greyed and shows a "Removed" badge. */
  removed?: boolean;
}

const ACTIVE_STATUSES = new Set(['live', 'active', 'approved']);

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function CampaignCard({
  variant,
  name,
  objective,
  status,
  monthlyBudget = 0,
  spend,
  impressions,
  clicks,
  conversions,
  cpa,
  launches,
  onClick,
  recSlot,
  removed = false,
}: CampaignCardProps) {
  const isActive = ACTIVE_STATUSES.has(status.toLowerCase());
  const hasData = spend > 0 || impressions > 0 || clicks > 0 || conversions > 0;
  const showMetrics = isActive && hasData;
  const computedCpa = cpa ?? (conversions > 0 ? spend / conversions : 0);
  const subtitleParts = [
    objective ? capitalize(objective) : null,
    monthlyBudget > 0 ? `$${Math.round(monthlyBudget).toLocaleString()}/mo` : null,
  ].filter(Boolean);

  return (
    <CampaignCardWrapper
      variant={variant}
      onClick={onClick}
      className={clsx(removed && styles.removedCard)}
    >
      <div className={styles.header}>
        <div className={patterns.cardName}>
          {name}
          {variant === 'pn' ? <PNBadge /> : <ExtBadge />}
        </div>
        {removed ? (
          <Badge variant="neutral">Removed</Badge>
        ) : (
          <CampaignStatusBadge status={status} />
        )}
      </div>
      {(subtitleParts.length > 0 || (launches && launches.length > 0)) && (
        <div className={styles.subtitleRow}>
          <div className={styles.subtitle}>{subtitleParts.join(' · ')}</div>
          {launches && launches.length > 0 && (
            <div className={styles.launches}>
              {launches
                .map((l) => `${channelLabel(l.channel)} (${formatDate(l.launchedAt)})`)
                .join(' · ')}
            </div>
          )}
        </div>
      )}
      {showMetrics && (
        <div className={styles.metricsGrid}>
          <Metric label="Spend (MTD)" value={spend > 0 ? formatCurrency(spend) : '—'} />
          <Metric label="Impr. (MTD)" value={impressions.toLocaleString()} />
          <Metric label="Clicks (MTD)" value={clicks.toLocaleString()} />
          <Metric label="Conv. (MTD)" value={formatConversions(conversions)} />
          <Metric label="CPA (MTD)" value={computedCpa > 0 ? formatCurrency(computedCpa) : '—'} />
        </div>
      )}
      {recSlot}
    </CampaignCardWrapper>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.metricItem}>
      <div className={styles.metricLabel}>{label}</div>
      <div className={styles.metricValue}>{value}</div>
    </div>
  );
}
